import { useEffect, useState } from "react";
import { userState } from "../../interface/MainInterface";
import { HttpUnAuthApi } from "../../interface/unauth-api";
import { CommonUtils } from "../../utils/common_utils";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Pagination, PaginationItem, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

// ChartJS
import BarChart from "../../utils/chart/BarChart";
import { DataGrid, GridColDef, gridPageCountSelector, gridPageSelector, GridPaginationModel, GridRenderCellParams, GridRowSelectionModel, GridRowsProp, useGridApiRef, useGridSelector } from "@mui/x-data-grid";
import { useWindowSize } from "../../utils/useWindowSize";
import MonthltyKeywordChart from "../report/chart/MonthltyKeywordChart";

import "./StatisticPage.css";

interface propsType {
  userState: userState;
}

interface DataItem {
  sk: string;
  count: number;
  pk: string;
  gi_pk: string;
  catagory?: string;
}

// const statisticApi = new HttpCustomApi(appConfig.statisticApiUrl);

type ActiveTab = "login" | "report" | "season" | "designer";
const TAB_DEFS: { key: ActiveTab; label: string }[] = [
  { key: "login",    label: "사용자 로그인 횟수" },
  { key: "report",   label: "레포트 발행 횟수" },
  { key: "season",   label: "시즌별 다운로드" },
  { key: "designer", label: "디자이너별 다운로드" },
];
  
const unAuthAPI = new HttpUnAuthApi();
const cUtils = new CommonUtils();
const fmtDate = (yyyymmdd: string) =>
  dayjs(yyyymmdd, "YYYYMMDD", true).isValid()
    ? dayjs(yyyymmdd, "YYYYMMDD").format("YYYY-MM-DD")
    : yyyymmdd;
  
const StatisticPage = (props: propsType) => {
  const windowSize = useWindowSize();
  const [isLoading, setIsLoading] = useState(false);
  const [srchDateFrom, setSrchDateFrom] = useState<Dayjs | null>(dayjs().startOf("week"));
  const [srchDateTo, setSrchDateTo] = useState<Dayjs | null>(dayjs().endOf("week"));
  const [statisticList, setStatisticList] = useState<any>([]);

  let pageSize: number = 6;
  const gridApi = useGridApiRef();
  const [dataRowsUser, setDataRowsUser] = useState<any>([]); // BarChart용
  const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] }); // Line용
  const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>([]);
  const [selectedPage, setSelectedPage] = useState<GridPaginationModel>({ pageSize: pageSize, page: 0 });
  const [isActive, setIsActive] = useState<ActiveTab>("login");

  const [dataGridRows, setDataGridRows] = useState<GridRowsProp>([]);
  // helper: 현재 탭에 맞는 컬럼 제목과 차트 라벨
  const metricTitle =
  isActive === "login"   ? "사용자 수" :
  isActive === "report"  ? "발행 횟수" :
  /* season / designer */  "다운로드 횟수";

  const labelTitles =
  isActive === "login"   ? ["사용자수"] :
  isActive === "report"  ? ["발행 횟수"] :
                          ["다운로드 횟수"];
  // pk 컬럼 제목
  const pkHeader =
    isActive === "season" ? "시즌" : isActive === "designer" ? "디자이너" : "구분";

  // 컬럼 (탭에 따라 동적)
  const baseSkCol: GridColDef = {
    field: "sk",
    headerName: "기준일",
    flex: 1,
    minWidth: 120,
    headerAlign: "center",
    align: "left",
    sortable: false,
    cellClassName: "right-border",
    headerClassName: "right-border header-bg",
    renderCell: (param: GridRenderCellParams) => {
      return (
        <Box>
          <Typography className="normal-font">{param.row.label}</Typography>
        </Box>
      );
    },
  };

  const baseCountCol: GridColDef = {
    field: "count",
    headerName: metricTitle,
    flex: 1,
    minWidth: 140,
    headerAlign: "center",
    align: "center",
    sortable: false,
    cellClassName: "right-border",
    headerClassName: "right-border header-bg",
    renderCell: (param: GridRenderCellParams) => {
      return (
        <Box>
          <Typography className="normal-font">{param.row.value}</Typography>
        </Box>
      );
    },
  };

  const pkCol: GridColDef = {
    field: "pk",
    headerName: pkHeader,
    flex: 1,
    minWidth: 180,
    headerAlign: "center",
    align: "left",
    sortable: false,
    cellClassName: "right-border",
    headerClassName: "right-border header-bg",
    renderCell: (param: GridRenderCellParams) => {
      return (
        <Box>
          <Typography className="normal-font">{param.row.pk}</Typography>
        </Box>
      );
    },
  };

  const columns: GridColDef[] =
    isActive === "season" || isActive === "designer"
      ? [baseSkCol, pkCol, baseCountCol]
      : [baseSkCol, baseCountCol];

  // 최초 마운트/언마운트
  useEffect(() => {
    get_statistic_list();
    return () => {
      setSrchDateFrom(dayjs().startOf("week"));
      setSrchDateTo(dayjs().endOf("week"));
      setStatisticList([]);
      setDataRowsUser([]);
      setSelectedRow([]);
      setSelectedPage({ pageSize: pageSize, page: 0 });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const aggregatedData = (data: DataItem[]) => {
      const aggregation = data.reduce<{[key:string]: number}>((acc, item) => {
        acc[item.sk] = (acc[item.sk] || 0) + item.count;
        return acc;
      }, {});
    
      // 차트 형식의 데이터 집계
      const userRows = Object.entries(aggregation)
        .map(([key, value]) => ({ label: key, value}))
        .sort((a, b) => new Date(b.label).getTime() - new Date(a.label).getTime());
      
      setDataRowsUser(userRows);

      // 그리드 형식의 데이터 집계
      const gridUserRows = [...userRows]
        .sort((a, b) => b.label.localeCompare(a.label)) // ← 내림차순 정렬
        .map((item) => ({
          id: item.label,
          label: item.label,
          value: item.value,
        }));
        // console.log("gridUserRows ", gridUserRows);
      setDataGridRows(gridUserRows);
    }

    aggregatedData(statisticList);
  }, [statisticList]);

  useEffect(() => {
    get_statistic_list();
  }, [isActive, srchDateFrom, srchDateTo]);

  const get_statistic_list = async () => {
    const param: any = {
      command: "get_statistic_list",
      search_date_from: dayjs(srchDateFrom).format("YYYYMMDD"),
      search_date_to: dayjs(srchDateTo).add(1, "day").format("YYYYMMDD"),
      sort: isActive,
    };

    setIsLoading(true);
    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      setStatisticList(res.response.statistic_list);
      // console.log("statisticlist ", res.response.statistic_list);
    }
    setIsLoading(false);
  };

  // 데이터 → (Bar or Line) + Grid 로 변환
  useEffect(() => {
    if (!Array.isArray(statisticList)) {
      setDataRowsUser([]);
      setChartData({ labels: [], datasets: [] });
      setDataGridRows([]);
      return;
    }

    if (isActive === "login" || isActive === "report") {
      buildSimpleView(statisticList);
    } else {
      buildGroupedView(statisticList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticList, isActive]);

  // 일자 합계 (BarChart + 2컬럼 그리드)
  const buildSimpleView = (data: DataItem[]) => {
    const byDate: Record<string, number> = {};
    for (const d of data) {
      byDate[d.sk] = (byDate[d.sk] ?? 0) + (d.count ?? 0);
    }

    const sortedKeys = Object.keys(byDate).sort((a, b) => a.localeCompare(b));
    const userRows = sortedKeys.map((k) => ({
      label: k,
      value: byDate[k],
    }));

    // BarChart용 (기존 형식 유지)
    setDataRowsUser(
      userRows
        .map((x) => ({ label: x.label, value: x.value }))
        .sort((a, b) => new Date(b.label).getTime() - new Date(a.label).getTime())
    );

    // Grid용
    const gridRows = [...sortedKeys]
      .sort((a, b) => b.localeCompare(a)) // 내림차순
      .map((k) => ({
        id: k,
        label: fmtDate(k),
        value: byDate[k],
      }));

    setDataGridRows(gridRows);
  };

  // pk별 시계열(LineChart + 3컬럼 그리드)
  const buildGroupedView = (data: DataItem[]) => {
    // 날짜/PK 수집
    const dateSet = new Set<string>();
    const pkSet = new Set<string>();
    for (const d of data) {
      dateSet.add(d.sk);
      if (d.pk) pkSet.add(d.pk);
    }
    const dates = Array.from(dateSet).sort((a, b) => a.localeCompare(b));
    const labels = dates.map(fmtDate);

    // pk별 날짜 매핑
    const mapByPk: Record<string, Record<string, number>> = {};
    Array.from(pkSet).forEach((p) => {
      mapByPk[p] = {};
    });
    
    for (const d of data) {
      if (!d.pk) continue;
      mapByPk[d.pk][d.sk] = (mapByPk[d.pk][d.sk] ?? 0) + (d.count ?? 0);
    }

    // datasets
    const datasets = Array.from(pkSet)
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({
        label: p,
        data: dates.map((dt) => mapByPk[p][dt] ?? 0),
        tension: 0.2,
        pointRadius: 2,
      }));

    setChartData({ labels, datasets });

    // Grid용 (기준일, pk, count) — 원본 정렬: 날짜 → pk
    const rows = data
      .slice()
      .sort((a, b) =>
        a.sk === b.sk
          ? (b.pk ?? "").localeCompare(a.pk ?? "")   // pk 내림차순
          : b.sk.localeCompare(a.sk)                 // 날짜(sk) 내림차순
      )
      .map((d, idx) => ({
        id: `${d.sk}|${d.pk ?? ""}|${idx}`,
        label: fmtDate(d.sk),
        pk: d.pk ?? "",
        value: d.count ?? 0,
      }));
    setDataGridRows(rows);
  };

  function CustomPagination() {
    const page = useGridSelector(gridApi, gridPageSelector);
    const pageCount = useGridSelector(gridApi, gridPageCountSelector);
    return (
      <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <Pagination
          color="primary"
          shape="rounded"
          count={pageCount === 0 ? 1 : pageCount}
          page={page + 1}
          siblingCount={10}
          onChange={(e, v) => gridApi.current.setPage(v - 1)}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              // next 버튼을 커스터마이즈
              components={{
                previous: (props) => (
                  <Stack direction="row" gap={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: "13px", color: '#2A2A2A' }}>이전</Typography>
                    <img src="images/btn-ghost-prev.png" alt="Prev" {...props} />
                  </Stack>
                ),
                next: (props) => (
                  <Stack direction="row" gap={2}>
                    <img src="images/btn-ghost-next.png" alt="Next" {...props} />
                    <Typography sx={{ fontWeight: 500, fontSize: "13px", color: '#2A2A2A' }}>다음</Typography>
                  </Stack>
                ),
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
                textDecorationLine: item.selected ? 'underline' : 'none',
                color: item.selected ? '#2A2A2A' : '#B1B1B1',
                userSelect: 'none',
                backgroundColor: 'transparent', // 배경 투명
                border: 'none', // 테두리 없음
                boxShadow: 'none', // 그림자 없음
                fontWeight: 500,
                minWidth: "23px",
                '&:hover': {
                  backgroundColor: 'transparent', // 호버 시 배경 투명
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent', // 선택된 상태 배경 제거
                  color: '#2A2A2A', // 선택된 상태 색상
                },
              }}
            />
          )}
        ></Pagination>
      </Box>
    );
  };

  return (
      <>
      <Box className="statistic-page">
        <section id="commWrap" style={{ padding: "99px 0px 0px", maxWidth:"1200px"}}>
          <Stack direction="column">
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '20px', marginBottom: 6 }}>AITB 이용 현황</Typography>
            </Box>
            <Stack direction={'row'} spacing={2} sx={{ alignItems: "center" }}>
              <Box sx={{ width: "400px", height: "44px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction={"row"} spacing={2}>
                    <DatePicker
                      showDaysOutsideCurrentMonth
                      format="YYYY-MM-DD"
                      value={srchDateFrom}
                      onChange={(newValue) => setSrchDateFrom(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          InputProps: {
                            style: {
                              borderRadius: "20px",
                            },
                          },
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography>{" ~ "}</Typography>
                    </Box>
                    <DatePicker
                      showDaysOutsideCurrentMonth
                      format="YYYY-MM-DD"
                      value={srchDateTo}
                      onChange={(newValue) => setSrchDateTo(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          InputProps: {
                            style: {
                              borderRadius: "20px",
                            },
                          },
                        },
                      }}
                    />
                  </Stack>
                </LocalizationProvider>
              </Box>

              <Box className="statistic-switch">
                <div className="statistic-wrap">
                  <div className="tabs">
                    {TAB_DEFS.map((t, idx) => {
                      const id = `switch-${idx + 1}`;
                      return (
                        <div key={t.key} className="tab">
                          <input
                            type="radio"
                            id={id}
                            name="tabs"
                            checked={isActive === t.key}
                            onChange={() => setIsActive(t.key)}
                          />
                          <label className="tab" htmlFor={id}>
                            {t.label}
                          </label>
                        </div>
                      );
                    })}
                    {/* 글라이더: 탭 위치에 따라 클래스 할당 */}
                    <span
                      className={`glider statistic${TAB_DEFS.findIndex(t => t.key === isActive) + 1}`}
                    ></span>
                  </div>
                </div>
              </Box>
              {/* <ToggleButtonGroup
                  value={isActive}
                  exclusive
                  onChange={() => {
                    setIsActive(isActive === "login" ? "report" : "login");
                  }}
                  aria-label="text alignment"
                  sx={{ height: "44px" }}
                >
                  <ToggleButton 
                    value={"login"} 
                    aria-label="LOGIN"
                    sx={{
                      borderRadius: "23px", // 버튼의 borderRadius 설정
                      border: '1px solid white',
                      backgroundColor: isActive === "login" ? "#D0FF00" : "#F0F0F0", // 클릭한 버튼 색상
                      color: isActive === "login" ? "#2A2A2A" : "#6A6A6A", // 텍스트 색상
                      '&.Mui-selected:hover': {
                          backgroundColor: isActive === "login" ? "#D0FF00" : "#E0E0E0", // 호버 색상
                      },
                      '&.Mui-selected': {
                          backgroundColor: "#D0FF00", // 선택된 버튼 색상
                          color: "#000", // 선택된 버튼 텍스트 색상
                      },
                      fontSize: "14px",
                      fontWeight: "600",
                      padding: "0 16px",
                  }}
                  >
                    사용자 로그인 횟수
                  </ToggleButton>
                  <ToggleButton 
                    value={"report"} 
                    aria-label="REPORT"
                    sx={{
                      borderRadius: "23px", // 버튼의 borderRadius 설정
                      border: '1px solid white',
                      backgroundColor: isActive === "report" ? "#D0FF00" : "#F0F0F0", // 클릭한 버튼 색상
                      color: isActive === "report" ? "#2A2A2A" : "#6A6A6A", // 텍스트 색상
                      '&.Mui-selected:hover': {
                          backgroundColor: isActive === "report" ? "#D0FF00" : "#E0E0E0", // 호버 색상
                      },
                      '&.Mui-selected': {
                          backgroundColor: "#D0FF00", // 선택된 버튼 색상
                          color: "#000", // 선택된 버튼 텍스트 색상
                      },
                      fontSize: "14px",
                      fontWeight: "600",
                      padding: "0 16px",
                  }}
                  >
                    레포트 발행 횟수
                  </ToggleButton>
                </ToggleButtonGroup> */}
            </Stack>
            <Stack direction={'row'} spacing={5} sx={{ width: "100%", alignItems: "start", marginTop: 4 }}>
              {/* 차트 */}
              <Paper className="statistic-box-wrapper">
                <Box sx={{ p: 3, overflowX: "auto", width: "616px" }}>
                  <Box sx={{ width: "100%", height: "350px", minWidth: "616px" }}>
                    {isActive === "login" || isActive === "report" ? (
                      <BarChart
                        dataRows={[dataRowsUser]}
                        chartTitle={{ display: false, text: "AITB 이용현황" }}
                        labelTitles={
                          isActive === "login" ? ["사용자수"] : ["발행 횟수"]
                        }
                        xName={"일자"}
                        yName={"건수"}
                        lineColors={null}
                        backgroundColors={null}
                        legend={{ display: true, position: "bottom" }}
                      />
                    ) : (
                      <MonthltyKeywordChart chartData={chartData} />
                    )}
                  </Box>
                </Box>
              </Paper>
              {/* data grid */}
              {/* <Box sx={{ height: { xs: "auto" }, p: 2, pt: 0 }}> */}
                <DataGrid
                  apiRef={gridApi}
                  rows={dataGridRows}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: pageSize,
                      },
                    },
                  }}
                  disableColumnMenu
                  slots={{ pagination: CustomPagination }}
                  hideFooterSelectedRowCount
                  getRowId={(row) => row.id}
                  rowSelectionModel={selectedRow}
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRow(newRowSelectionModel);
                  }}
                  paginationModel={selectedPage}
                  onPaginationModelChange={(newPagenationModel) => {
                    setSelectedPage(newPagenationModel);
                  }}
                  columnHeaderHeight={44}
                  rowHeight={44}
                  sx={{ 
                    mt: 2,
                    border: 'none',
                    borderRadius: '0px',
                    height: 44 * 9, // 고정 높이 설정: 8개의 행 + 헤더 높이 + 페이지네이션 높이
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F5F5F5", borderColor: '#000' },
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: '14px' },
                    "& .MuiDataGrid-footerContainer": { borderTop: 'none' },
                    "& .MuiDataGrid-cell": { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                  }}
                />
              {/* </Box> */}
            </Stack>
          </Stack>
        </section>
      </Box>
      </>
  );
};

export default StatisticPage;